import { useMemo, useState } from 'react';
import { BarChart } from '../charts/BarChart';
import { BarList, ChartCard, Drawer, Segmented, Stat } from '../components/data';
import { Gauge } from '../components/Gauge';
import { ErrorBanner, Field, Loading } from '../components/ui';
import { downloadCsv, fileStamp } from '../lib/download';
import { formatNumber, num, periodLabel, shortDate, toLocalDateString, todayLocal } from '../lib/format';
import { useHeldResource, useResource } from '../lib/hooks';

interface AtProject {
  id: number;
  name: string;
  number: string | null;
  company: string | null;
  lead: string | null;
  status: string;
  done: boolean;
  type: string | null;
  department: string | null;
  start: string | null;
  end: string | null;
  completed: string | null;
  created: string | null;
  lastActivity: string | null;
  percentComplete: number | null;
  estimatedHours: number | null;
  actualHours: number | null;
  billedHours: number | null;
  estimatedRevenue: number | null;
  estimatedLaborCost: number | null;
  originalRevenue: number | null;
  changeOrderRevenue: number | null;
  costsBudget: number | null;
  costsRevenue: number | null;
  statusDetail: string | null;
  tasks: { open: number; overdue: number; total: number } | null;
  /* The newest Project Status note, for open projects only. */
  latestNote: { title: string; text: string; date: string | null; by: string | null } | null;
}
interface AtTask {
  id: number;
  title: string;
  number: string | null;
  status: string;
  done: boolean;
  assignedTo: string | null;
  start: string | null;
  end: string | null;
  estimatedHours: number | null;
  remainingHours: number | null;
}

/* One time entry against a project task, from /projects/autotask/time — only
   the fields the Hours used tile counts. */
interface AtTimeEntry {
  date: string | null;
  projectId: number | null;
  projectType: string | null;
  hours: number;
  billable: number;
}

type Focus = 'all' | 'overdue' | 'overHours' | 'stale' | 'overdueTasks';
const FOCUS_LABEL: Record<Focus, string> = {
  all: 'All',
  overdue: 'Past their end date',
  overHours: 'Over their hours estimate',
  stale: 'No activity in 30 days',
  overdueTasks: 'With overdue tasks',
};

/* The dashboard is a picture of now unless a period is chosen, so 'all' is
   the default and nothing is hidden until somebody asks for a window. */
type Range = 'all' | 'week' | 'lastWeek' | 'month' | 'lastMonth' | 'quarter' | '90d' | 'ytd' | 'lastYear' | 'custom';
const RANGES: { id: Range; label: string }[] = [
  { id: 'all', label: 'All time' },
  { id: 'week', label: 'This week' },
  { id: 'lastWeek', label: 'Last week' },
  { id: 'month', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
  { id: 'quarter', label: 'This quarter' },
  { id: '90d', label: '90 days' },
  { id: 'ytd', label: 'This year' },
  { id: 'lastYear', label: 'Last year' },
  { id: 'custom', label: 'Custom' },
];

/** The days a range covers, first and last inclusive, in this browser's calendar.
 *  Weeks start Monday, as they do on Time by resource. */
function rangeDays(range: Exclude<Range, 'custom' | 'all'>): [string, string] {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const y = d.getFullYear();
  if (range === 'week' || range === 'lastWeek') {
    const monday = new Date(d);
    monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    if (range === 'week') return [toLocalDateString(monday), todayLocal()];
    const start = new Date(monday);
    start.setDate(start.getDate() - 7);
    const end = new Date(monday);
    end.setDate(end.getDate() - 1);
    return [toLocalDateString(start), toLocalDateString(end)];
  }
  if (range === 'month') return [toLocalDateString(new Date(y, d.getMonth(), 1)), todayLocal()];
  if (range === 'lastMonth')
    return [toLocalDateString(new Date(y, d.getMonth() - 1, 1)), toLocalDateString(new Date(y, d.getMonth(), 0))];
  if (range === 'quarter') return [toLocalDateString(new Date(y, Math.floor(d.getMonth() / 3) * 3, 1)), todayLocal()];
  if (range === 'ytd') return [`${y}-01-01`, todayLocal()];
  if (range === 'lastYear') return [`${y - 1}-01-01`, `${y - 1}-12-31`];
  const start = new Date(d);
  start.setDate(start.getDate() - 89);
  return [toLocalDateString(start), todayLocal()];
}

const dayOf = (iso: string | null) => (iso ? iso.slice(0, 10) : null);

/* shortDate leaves the year off, which is fine inside one year and no use
   for a period that straddles one ("1 Jan - 31 Dec" of which year?). Built
   from the string rather than a Date so a UTC midnight cannot slip a day
   backwards west of Greenwich. */
const dayLabel = (iso: string) => `${shortDate(iso)} ${iso.slice(0, 4)}`;

/**
 * Was the project running at any point in the window? Its start is the start
 * date, or failing that the day it was created; its finish is the day it was
 * completed, or failing that its end date. An open project with no end date
 * has not finished, so it counts in every window that has begun by now — and
 * a project with neither end of its life recorded is kept rather than dropped
 * on a date Autotask never had.
 */
function activeIn(p: AtProject, from: string, to: string): boolean {
  const start = dayOf(p.start) ?? dayOf(p.created);
  const end = dayOf(p.completed) ?? dayOf(p.end);
  if (start !== null && start > to) return false;
  if (end !== null && end < from) return false;
  return true;
}

/** Was it finished inside the window? */
const completedIn = (p: AtProject, from: string, to: string) => {
  const c = dayOf(p.completed);
  return c !== null && c >= from && c <= to;
};

/**
 * The months the completions chart covers: the ones the window spans, the most
 * recent twenty-four at most so the bars stay readable, or the last twelve
 * when no window is chosen.
 */
function chartMonths(from: string | null, to: string | null): string[] {
  const out: string[] = [];
  const last = to ? new Date(`${to}T00:00:00`) : new Date();
  const first = from ? new Date(`${from}T00:00:00`) : null;
  const floor = first ? new Date(first.getFullYear(), first.getMonth(), 1) : null;
  const d = new Date(last.getFullYear(), last.getMonth(), 1);
  for (let i = 0; i < 24; i++) {
    out.unshift(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    if (floor ? d.getTime() <= floor.getTime() : out.length === 12) break;
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

/**
 * Cycle time: whole days from a project's start to the day it was completed.
 * The start is its start date, or failing that the day it was created — the
 * same rule activeIn uses. Null when either end is missing, or when Autotask
 * has it completing before it began, which is a typo rather than a fast job.
 */
function cycleDays(p: AtProject): number | null {
  const start = dayOf(p.start) ?? dayOf(p.created);
  const done = dayOf(p.completed);
  if (!start || !done) return null;
  const days = Math.round((Date.parse(`${done}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000);
  return days < 0 ? null : days;
}

/** Whole days between two YYYY-MM-DD days, parsed as UTC so a local midnight
 *  cannot slip one either way. */
const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

/**
 * Days the project has been running: from its start to the day it was
 * completed, or to today while it is still open. The start is its start date,
 * or failing that the day it was created — the same rule activeIn and
 * cycleDays use. For a completed project this is exactly cycleDays, so the
 * column and the cycle time gauge cannot disagree about the same project.
 *
 * Null when there is no start, when a finished project has no completed date,
 * or when Autotask has it completing before it began.
 */
function elapsedDays(p: AtProject, today: string): number | null {
  const start = dayOf(p.start) ?? dayOf(p.created);
  const until = p.done ? dayOf(p.completed) : today;
  if (!start || !until) return null;
  const days = daysBetween(start, until);
  return days < 0 ? null : days;
}

/**
 * Days left to the end date, counted from today: negative once it has gone by.
 * Autotask holds no forecast, so the end date is the only estimate there is —
 * this is the date talking, not a projection from the work done.
 *
 * Null for a project already finished (nothing left to estimate) and for one
 * with no end date, which is not the same as "due today".
 */
function etaDays(p: AtProject, today: string): number | null {
  const end = dayOf(p.end);
  if (p.done || !end) return null;
  return daysBetween(today, end);
}

const median = (xs: number[]) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2;
};

const money = (v: number | null) => (v === null ? '—' : formatNumber(v, 'currency'));
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const burn = (p: AtProject) => (p.estimatedHours && p.actualHours !== null ? p.actualHours / p.estimatedHours : null);

export type { AtProject };

/**
 * The headline figures over a set of projects: open, past end date, over
 * hours, with a late task, gone quiet, and hours used against the estimate on
 * the open ones. Shared with the home dashboard's Projects card, so the card
 * and this page count the same projects the same way.
 */
export function projectHeadlines(projects: AtProject[], now = Date.now()) {
  const open = projects.filter((p) => !p.done);
  return {
    open: open.length,
    overdue: open.filter((p) => p.end !== null && Date.parse(p.end) < now).length,
    overHours: open.filter((p) => (burn(p) ?? 0) > 1).length,
    stale: open.filter((p) => p.lastActivity === null || now - Date.parse(p.lastActivity) > 30 * 86_400_000).length,
    overdueTasks: open.filter((p) => (p.tasks?.overdue ?? 0) > 0).length,
    est: open.reduce((n, p) => n + (p.estimatedHours ?? 0), 0),
    used: open.reduce((n, p) => n + (p.actualHours ?? 0), 0),
  };
}

/**
 * The Projects module's Autotask dashboard: every project in Autotask (not its
 * templates), what is open, what is late or over its hours, who leads what,
 * and what has finished — each figure a filter on the list, each project
 * openable for its tasks. Read once and kept until Refresh.
 */
export function ProjectsAutotaskDashboard() {
  const data = useHeldResource<{ projects: AtProject[]; excluded: number; notesError?: string | null; fetchedUtc: string }>('/projects/autotask');
  const [show, setShow] = useState<'open' | 'done' | 'all'>('open');
  const [focus, setFocus] = useState<Focus>('all');
  const [lead, setLead] = useState('');
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<AtProject | null>(null);
  /* Client or internal work: an Autotask project type that says Internal is
     internal, everything else is client work. Scopes the whole dashboard. */
  const [kind, setKind] = useState<'all' | 'client' | 'internal'>('all');
  /* The period the dashboard is read over. Autotask is asked for every
     project once, so this is a filter over what is already here rather than
     another read. */
  const [range, setRange] = useState<Range>('all');
  const [customFrom, setCustomFrom] = useState(() => rangeDays('ytd')[0]);
  const [customTo, setCustomTo] = useState(() => rangeDays('ytd')[1]);

  const [from, to] = range === 'all' ? [null, null] : range === 'custom' ? [customFrom, customTo] : rangeDays(range);
  const validRange = from === null || to === null || from <= to;

  const raw = data.data?.projects;
  const isInternal = (p: AtProject) => /internal/i.test(p.type ?? '');
  /* The window first, so the Client / Internal counts describe the period on
     screen rather than the whole of Autotask. */
  const ranged = useMemo(
    () => (raw ?? []).filter((p) => from === null || to === null || !validRange || activeIn(p, from, to)),
    [raw, from, to, validRange],
  );
  const all = useMemo(
    () => ranged.filter((p) => kind === 'all' || (kind === 'internal' ? isInternal(p) : !isInternal(p))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ranged, kind],
  );
  const counts = useMemo(() => {
    const internal = ranged.filter(isInternal).length;
    return { all: ranged.length, internal, client: ranged.length - internal };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ranged]);
  /* Hours used over a period cannot come from the projects: a project's
     actualHours is its whole life, so filtering the project list to "this
     month" still adds up every hour ever logged against the projects that
     survive the filter — which is why the tile never moved. The hours
     actually worked in the window come from the time entries, which carry
     the day they were worked. The API reads a year at most, so a longer
     custom window falls back to the lifetime figure rather than erroring. */
  const periodDays =
    from !== null && to !== null && validRange
      ? Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1
      : 0;
  const overAYear = periodDays > 366;
  const timePath =
    from !== null && to !== null && validRange && !overAYear
      ? `/projects/autotask/time?${new URLSearchParams({ from, to })}`
      : null;
  const time = useHeldResource<{ entries: AtTimeEntry[]; fetchedUtc: string }>(timePath);
  /* Client or internal is read off the entry's project type, the same test the
     dashboard runs on the project. Time on projects that have since finished
     is counted: it was worked in the window, so it was used in the window. */
  const periodHours = useMemo(() => {
    const entries = (time.data?.entries ?? []).filter(
      (e) => kind === 'all' || (kind === 'internal' ? /internal/i.test(e.projectType ?? '') : !/internal/i.test(e.projectType ?? '')),
    );
    return {
      hours: entries.reduce((n, e) => n + e.hours, 0),
      billable: entries.reduce((n, e) => n + e.billable, 0),
      projects: new Set(entries.map((e) => e.projectId)).size,
    };
  }, [time.data, kind]);

  const now = Date.now();
  /* Today as a plain day, for the two day-count columns: they are whole days
     on the calendar, not a difference between two instants. */
  const today = todayLocal();
  const isOverdue = (p: AtProject) => !p.done && p.end !== null && Date.parse(p.end) < now;
  const isOverHours = (p: AtProject) => (burn(p) ?? 0) > 1;
  const isStale = (p: AtProject) => !p.done && (p.lastActivity === null || now - Date.parse(p.lastActivity) > 30 * 86_400_000);
  const hasOverdueTasks = (p: AtProject) => (p.tasks?.overdue ?? 0) > 0;
  const openProjects = all.filter((p) => !p.done);

  const stats = useMemo(() => {
    const year = new Date().getFullYear();
    return {
      ...projectHeadlines(all, now),
      revenue: openProjects.reduce((n, p) => n + (p.estimatedRevenue ?? 0), 0),
      /* In a window, "completed" means completed inside it; with no window
         it stays the calendar year, which is what the tile has always said. */
      completed:
        from !== null && to !== null
          ? all.filter((p) => completedIn(p, from, to)).length
          : all.filter((p) => p.done && p.completed && new Date(p.completed).getFullYear() === year).length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, from, to]);

  /* Cycle time over the same projects the Completed tile counts: finished in
     the period, or this calendar year when no period is chosen. */
  const cycle = useMemo(() => {
    const year = new Date().getFullYear();
    const finished = all.filter((p) =>
      from !== null && to !== null
        ? completedIn(p, from, to)
        : p.done && p.completed !== null && new Date(p.completed).getFullYear() === year,
    );
    const days = finished.map(cycleDays).filter((d): d is number => d !== null);
    const avg = days.length ? days.reduce((n, d) => n + d, 0) / days.length : null;
    return {
      avg,
      median: median(days),
      measured: days.length,
      unmeasured: finished.length - days.length,
      fastest: days.length ? Math.min(...days) : null,
      slowest: days.length ? Math.max(...days) : null,
    };
  }, [all, from, to]);
  /* A scale that leaves the needle room either side: at least 60 days, else
     twice the average rounded up to a month. */
  const cycleMax = Math.max(60, Math.ceil(((cycle.avg ?? 0) * 2) / 30) * 30);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all
      .filter((p) => (show === 'open' ? !p.done : show === 'done' ? p.done : true))
      .filter((p) =>
        focus === 'overdue' ? isOverdue(p) : focus === 'overHours' ? isOverHours(p) : focus === 'stale' ? isStale(p) : focus === 'overdueTasks' ? hasOverdueTasks(p) : true,
      )
      .filter((p) => (!lead || (p.lead ?? 'No lead') === lead) && (!status || p.status === status) && (!type || (p.type ?? 'None') === type))
      .filter((p) => !q || [p.name, p.number, p.company, p.lead].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)))
      .sort((a, b) => String(a.end ?? '9').localeCompare(String(b.end ?? '9')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, show, focus, lead, status, type, search]);

  const tally = (list: AtProject[], key: (p: AtProject) => string) => {
    const m = new Map<string, number>();
    for (const p of list) m.set(key(p), (m.get(key(p)) ?? 0) + 1);
    return [...m].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  };
  const byStatus = tally(openProjects, (p) => p.status);
  const byLead = tally(openProjects, (p) => p.lead ?? 'No lead');
  const byType = tally(openProjects, (p) => p.type ?? 'None');

  /* Projects completed per month, over the window or the last twelve. */
  const completions = useMemo(() => {
    const out = chartMonths(from, to).map((month) => ({ month, completed: 0 }));
    const at = new Map(out.map((r) => [r.month, r]));
    for (const p of all) {
      if (!p.completed) continue;
      const c = new Date(p.completed);
      const r = at.get(`${c.getFullYear()}-${String(c.getMonth() + 1).padStart(2, '0')}`);
      if (r) r.completed += 1;
    }
    return out;
  }, [all, from, to]);

  const pick = (f: Focus) => {
    setShow('open');
    setFocus(focus === f ? 'all' : f);
  };
  const pickList = (setter: (v: string) => void, current: string, v: string) => {
    setShow('open');
    setter(current === v ? '' : v);
  };

  if (data.loading) return <Loading label="Reading projects from Autotask" />;
  if (data.error) return <ErrorBanner message={data.error} />;
  if (!data.data) return null;

  const exportCsv = () =>
    downloadCsv(`autotask_projects_${fileStamp()}.csv`, [
      ['Project', 'Number', 'Company', 'Lead', 'Status', 'Type', 'Start', 'End', 'Completed', 'Completed days', 'ETA days', '% complete', 'Estimated hours', 'Actual hours', 'Estimated revenue', 'Open tasks', 'Overdue tasks', 'Last activity'],
      ...rows.map((p) => [
        p.name, p.number, p.company, p.lead, p.status, p.type, p.start?.slice(0, 10), p.end?.slice(0, 10), p.completed?.slice(0, 10),
        elapsedDays(p, today) ?? '', etaDays(p, today) ?? '',
        p.percentComplete, p.estimatedHours, p.actualHours, p.estimatedRevenue, p.tasks?.open ?? '', p.tasks?.overdue ?? '', p.lastActivity?.slice(0, 10),
      ]),
    ]);

  /* A window longer than two years is cut to its most recent two for the
     chart, which the subtitle says rather than quietly drawing less. */
  const monthsInRange =
    from && to ? (Number(to.slice(0, 4)) - Number(from.slice(0, 4))) * 12 + (Number(to.slice(5, 7)) - Number(from.slice(5, 7))) + 1 : 0;
  const chartSubtitle =
    from && to && validRange
      ? monthsInRange > 24
        ? 'The most recent twenty-four months of the period.'
        : `${dayLabel(from)} – ${dayLabel(to)}.`
      : 'The last twelve months.';

  return (
    <div className="mt-4 pa-dash">
      <div className="row wrap mb-2" style={{ alignItems: 'flex-end', gap: 'var(--space-4)' }}>
        <Field label="Period">
          <Segmented label="Period" options={RANGES} value={range} onChange={setRange} />
        </Field>
        {range === 'custom' && (
          <>
            <Field label="From">
              <input className="input" type="date" value={customFrom} max={customTo} onChange={(e) => setCustomFrom(e.target.value)} />
            </Field>
            <Field label="To">
              <input className="input" type="date" value={customTo} min={customFrom} onChange={(e) => setCustomTo(e.target.value)} />
            </Field>
          </>
        )}
      </div>
      {!validRange && <ErrorBanner message="The start date is after the end date." />}
      <div className="row-between wrap mb-2">
        <p className="xs muted">
          {num(all.length)} {kind === 'all' ? '' : `${kind} `}project{all.length === 1 ? '' : 's'} in Autotask
          {from && to && validRange ? `, running between ${dayLabel(from)} and ${dayLabel(to)}` : ''}
          {data.data.excluded ? ` (${num(data.data.excluded)} templates, baselines and archived copies left out)` : ''} · as of{' '}
          {new Date(data.fetchedAt ?? data.data.fetchedUtc).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
        </p>
        <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
          <Segmented
            label="Client or internal"
            options={[
              { id: 'all', label: `All (${counts.all})` },
              { id: 'client', label: `Client (${counts.client})` },
              { id: 'internal', label: `Internal (${counts.internal})` },
            ]}
            value={kind}
            onChange={(v) => {
              setKind(v);
              setType('');
            }}
          />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={data.refreshing || time.refreshing}
            onClick={() => {
              data.refresh();
              time.refresh();
            }}
          >
            {data.refreshing || time.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
          </button>
        </div>
      </div>
      {from && to && validRange && (
        <p className="xs muted mb-2">
          A project is in the period if it was running at any point in it. Hours used counts the time actually
          logged inside it. Past end date, Over hours and Gone quiet still read against today — they say what is
          late now, not what was late then.
        </p>
      )}
      {data.data.notesError && <ErrorBanner message={data.data.notesError} />}

      <div className="grid grid-4 mb-4 pa-tiles">
        <button type="button" className={`pa-tile${focus === 'all' && show === 'open' && !lead && !status && !type ? ' is-on' : ''}`} onClick={() => { setShow('open'); setFocus('all'); setLead(''); setStatus(''); setType(''); }}>
          <Stat label="Open projects" value={num(stats.open)} foot={`${money(stats.revenue)} estimated revenue`} />
        </button>
        <button type="button" className={`pa-tile${focus === 'overdue' ? ' is-on' : ''}`} onClick={() => pick('overdue')}>
          <Stat label="Past end date" value={num(stats.overdue)} flagged={stats.overdue > 0} foot="open, end date gone by" />
        </button>
        <button type="button" className={`pa-tile${focus === 'overHours' ? ' is-on' : ''}`} onClick={() => pick('overHours')}>
          <Stat label="Over hours" value={num(stats.overHours)} flagged={stats.overHours > 0} foot="actual hours above the estimate" />
        </button>
        <button type="button" className={`pa-tile${focus === 'overdueTasks' ? ' is-on' : ''}`} onClick={() => pick('overdueTasks')}>
          <Stat label="Overdue tasks" value={num(stats.overdueTasks)} flagged={stats.overdueTasks > 0} foot="open projects with a late task" />
        </button>
        <button type="button" className={`pa-tile${focus === 'stale' ? ' is-on' : ''}`} onClick={() => pick('stale')}>
          <Stat label="Gone quiet" value={num(stats.stale)} foot="no activity in 30 days" />
        </button>
        <div className="pa-tile is-static">
          {timePath ? (
            <Stat
              label="Hours used in period"
              value={time.loading ? '…' : time.error ? '—' : num(periodHours.hours, 0)}
              foot={
                time.error
                  ? time.error
                  : time.loading
                    ? 'reading time entries from Autotask'
                    : `${num(periodHours.billable, 0)} billable · ${num(periodHours.projects)} project${periodHours.projects === 1 ? '' : 's'} worked on`
              }
            />
          ) : (
            <Stat
              label="Hours used"
              value={num(stats.used, 0)}
              foot={
                overAYear
                  ? 'all time — hours in a period are read over a year at most'
                  : stats.est
                    ? `of ${num(stats.est, 0)} estimated · ${Math.round((stats.used / stats.est) * 100)}%`
                    : 'no estimates'
              }
            />
          )}
        </div>
        <button type="button" className={`pa-tile${show === 'done' ? ' is-on' : ''}`} onClick={() => { setFocus('all'); setShow(show === 'done' ? 'open' : 'done'); }}>
          <Stat
            label={from && to && validRange ? 'Completed in period' : 'Completed this year'}
            value={num(stats.completed)}
            foot="click for completed projects"
          />
        </button>
      </div>

      <div className="grid grid-4 mb-4">
        <ChartCard
          title="Project cycle time"
          subtitle={`Days from start to complete, for projects completed ${from && to && validRange ? 'in the period' : 'this year'}.`}
        >
          <Gauge
            label="Average cycle time"
            value={cycle.avg === null ? null : Math.round(cycle.avg)}
            target={null}
            max={cycleMax}
            unit=" days"
            lowerIsBetter
            hint={
              cycle.measured
                ? `median ${Math.round(cycle.median ?? 0)} days · ${num(cycle.measured)} project${cycle.measured === 1 ? '' : 's'}`
                : 'no completed projects to measure'
            }
            onClick={
              cycle.measured
                ? () => {
                    setFocus('all');
                    setShow('done');
                  }
                : undefined
            }
            drillLabel="See the completed projects"
          />
          {cycle.measured > 0 && (
            <p className="xs muted" style={{ textAlign: 'center' }}>
              Fastest {num(cycle.fastest)} days · slowest {num(cycle.slowest)} days
              {cycle.unmeasured > 0 && ` · ${num(cycle.unmeasured)} left out with no start or completed date`}
            </p>
          )}
        </ChartCard>
        <ChartCard title="Open by status" subtitle="Click one to filter the list.">
          <div className="pa-pick">
            {byStatus.map((s) => (
              <button key={s.name} type="button" className={status === s.name ? 'is-on' : undefined} onClick={() => pickList(setStatus, status, s.name)}>
                <BarList rows={[{ name: s.name, value: s.value, display: num(s.value) }]} />
              </button>
            ))}
          </div>
        </ChartCard>
        <ChartCard title="Open by project lead" subtitle="Click one to filter the list.">
          <div className="pa-pick">
            {byLead.map((s) => (
              <button key={s.name} type="button" className={lead === s.name ? 'is-on' : undefined} onClick={() => pickList(setLead, lead, s.name)}>
                <BarList rows={[{ name: s.name, value: s.value, display: num(s.value) }]} />
              </button>
            ))}
          </div>
        </ChartCard>
        <ChartCard title="Open by type" subtitle="Click one to filter the list.">
          <div className="pa-pick">
            {byType.map((s) => (
              <button key={s.name} type="button" className={type === s.name ? 'is-on' : undefined} onClick={() => pickList(setType, type, s.name)}>
                <BarList rows={[{ name: s.name, value: s.value, display: num(s.value) }]} />
              </button>
            ))}
          </div>
        </ChartCard>
      </div>

      <ChartCard
        className="mb-4"
        title="Projects completed by month"
        subtitle={chartSubtitle}
      >
        <BarChart
          data={completions}
          xKey="month"
          series={[{ key: 'completed', label: 'Completed', color: 'var(--series-1)' }]}
          xFormat={periodLabel}
          yFormat={(v) => num(v, 0)}
          height={200}
          emptyMessage="Nothing completed in the last year."
        />
      </ChartCard>

      <section className="card">
        <div className="card-head">
          <div className="grow">
            <h3>
              {rows.length} {show === 'done' ? 'completed' : show === 'open' ? 'open' : ''} project{rows.length === 1 ? '' : 's'}
              {focus !== 'all' ? ` — ${FOCUS_LABEL[focus].toLowerCase()}` : ''}
              {[lead, status, type].filter(Boolean).length ? ` — ${[lead, status, type].filter(Boolean).join(' · ')}` : ''}
            </h3>
            <p className="sub">
              Soonest end date first. Click a project for its tasks. {num(openProjects.filter((p) => p.latestNote).length)} of{' '}
              {num(openProjects.length)} open projects have a Project Status note.
            </p>
          </div>
          <div className="row wrap" style={{ alignItems: 'flex-end' }}>
            <Field label="Search">
              <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, number, company, lead" />
            </Field>
            <Field label="Show">
              <Segmented
                label="Show"
                options={[
                  { id: 'open', label: 'Open' },
                  { id: 'done', label: 'Completed' },
                  { id: 'all', label: 'All' },
                ]}
                value={show}
                onChange={setShow}
              />
            </Field>
            {(focus !== 'all' || lead || status || type) && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setFocus('all'); setLead(''); setStatus(''); setType(''); }}>
                Clear filters
              </button>
            )}
            <button type="button" className="btn btn-secondary btn-sm" disabled={rows.length === 0} onClick={exportCsv}>
              Export CSV
            </button>
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="card-body">
            <p className="muted small">No projects match.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table pa-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Company</th>
                  <th>Lead</th>
                  <th>Status</th>
                  <th>End</th>
                  <th className="num">Completed days</th>
                  <th className="num">ETA days</th>
                  <th className="num">Done</th>
                  <th className="num">Hours</th>
                  <th className="num">Tasks</th>
                  <th className="num">Est. revenue</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const b = burn(p);
                  const ran = elapsedDays(p, today);
                  const eta = etaDays(p, today);
                  return (
                    <tr key={p.id}>
                      <td className="pa-proj">
                        <button type="button" className="link-button" onClick={() => setOpen(p)}>
                          {p.name || '(untitled)'}
                        </button>
                        <div className="xs muted">
                          {[p.number, p.type].filter(Boolean).join(' · ')}
                        </div>
                        {/* The latest Project Status note, under the name so it is never off-screen. */}
                        {!p.done && (
                          <div className={`pa-note${p.latestNote ? '' : ' is-empty'}`}>
                            {p.latestNote ? (
                              <>
                                <div className="pa-note-head">
                                  Status note · {day(p.latestNote.date)}
                                  {p.latestNote.by ? ` · ${p.latestNote.by}` : ''}
                                </div>
                                {p.latestNote.title && <div className="small strong">{p.latestNote.title}</div>}
                                {p.latestNote.text && (
                                  <div className="xs pa-note-text" title={p.latestNote.text}>
                                    {p.latestNote.text}
                                  </div>
                                )}
                              </>
                            ) : (
                              'No status note'
                            )}
                          </div>
                        )}
                      </td>
                      <td>{p.company ?? '—'}</td>
                      <td>{p.lead ?? <span className="subtle">No lead</span>}</td>
                      <td>{p.status}</td>
                      <td className="xs pa-end" style={isOverdue(p) ? { color: 'var(--status-danger)', fontWeight: 600 } : undefined}>
                        {day(p.done ? p.completed ?? p.end : p.end)}
                        {isOverdue(p) && <div>Past due</div>}
                      </td>
                      <td className="num">
                        {ran === null ? '—' : num(ran)}
                        {ran !== null && !p.done && <div className="xs muted">so far</div>}
                      </td>
                      <td className="num" style={eta !== null && eta < 0 ? { color: 'var(--status-danger)', fontWeight: 600 } : undefined}>
                        {eta === null ? '—' : num(eta)}
                        {eta !== null && eta < 0 && <div className="xs">overdue</div>}
                      </td>
                      <td className="num">{p.percentComplete === null ? '—' : `${Math.round(p.percentComplete)}%`}</td>
                      <td className="num" style={b !== null && b > 1 ? { color: 'var(--status-danger)' } : undefined}>
                        {num(p.actualHours ?? 0, 1)} / {p.estimatedHours === null ? '—' : num(p.estimatedHours, 1)}
                        {b !== null && <div className="xs muted">{Math.round(b * 100)}%</div>}
                      </td>
                      <td className="num">
                        {p.tasks ? `${p.tasks.open} open` : '—'}
                        {p.tasks && p.tasks.overdue > 0 && <div className="xs" style={{ color: 'var(--status-danger)' }}>{p.tasks.overdue} overdue</div>}
                      </td>
                      <td className="num">{money(p.estimatedRevenue)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {open && <ProjectDrawer project={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

/** One project: its figures and its tasks, read when opened. */
function ProjectDrawer({ project: p, onClose }: { project: AtProject; onClose: () => void }) {
  const tasks = useResource<AtTask[]>(`/projects/autotask/${p.id}/tasks`);
  const b = burn(p);
  const now = Date.now();
  return (
    <Drawer open wide onClose={onClose} title={p.name || 'Project'} subtitle={[p.number, p.company, p.status].filter(Boolean).join(' · ')}>
      <div className="grid grid-3 mb-4">
        <Stat label="Complete" value={p.percentComplete === null ? '—' : `${Math.round(p.percentComplete)}%`} foot={`${day(p.start)} → ${day(p.end)}`} />
        <Stat
          label="Hours"
          value={`${num(p.actualHours ?? 0, 1)}`}
          unit={p.estimatedHours ? ` / ${num(p.estimatedHours, 1)}` : ''}
          flagged={b !== null && b > 1}
          foot={b === null ? 'no estimate' : `${Math.round(b * 100)}% of estimate · ${num(p.billedHours ?? 0, 1)} billed`}
        />
        <Stat
          label="Estimated revenue"
          value={money(p.estimatedRevenue)}
          foot={`labor cost est. ${money(p.estimatedLaborCost)}${p.changeOrderRevenue ? ` · change orders ${money(p.changeOrderRevenue)}` : ''}`}
        />
      </div>
      <dl className="pa-facts mb-4">
        <dt>Lead</dt>
        <dd>{p.lead ?? '—'}</dd>
        <dt>Type</dt>
        <dd>{p.type ?? '—'}</dd>
        <dt>Department</dt>
        <dd>{p.department ?? '—'}</dd>
        <dt>Last activity</dt>
        <dd>{day(p.lastActivity)}</dd>
        {p.latestNote && (
          <>
            <dt>Latest status note</dt>
            <dd>
              <div className="xs muted">
                {day(p.latestNote.date)}
                {p.latestNote.by ? ` · ${p.latestNote.by}` : ''}
              </div>
              {p.latestNote.title && <div className="strong">{p.latestNote.title}</div>}
              <div style={{ whiteSpace: 'pre-wrap' }}>{p.latestNote.text}</div>
            </dd>
          </>
        )}
        {p.statusDetail && (
          <>
            <dt>Status note</dt>
            <dd>{p.statusDetail}</dd>
          </>
        )}
      </dl>
      <h4 className="mb-2">Tasks</h4>
      {tasks.loading && <Loading label="Reading the tasks from Autotask" />}
      {tasks.error && <ErrorBanner message={tasks.error} />}
      {tasks.data && tasks.data.length === 0 && <p className="muted small">No tasks on this project.</p>}
      {tasks.data && tasks.data.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Task</th>
                <th>Assigned</th>
                <th>Status</th>
                <th>Due</th>
                <th className="num">Est. / left</th>
              </tr>
            </thead>
            <tbody>
              {tasks.data.map((t) => {
                const late = !t.done && t.end !== null && Date.parse(t.end) < now;
                return (
                  <tr key={t.id} className={t.done ? 'muted' : undefined}>
                    <td>
                      {t.title}
                      {t.number && <div className="xs muted mono">{t.number}</div>}
                    </td>
                    <td>{t.assignedTo ?? <span className="subtle">Unassigned</span>}</td>
                    <td>{t.status}</td>
                    <td className="xs" style={late ? { color: 'var(--status-danger)', fontWeight: 600 } : undefined}>
                      {day(t.end)}
                      {late && <div>Overdue</div>}
                    </td>
                    <td className="num">
                      {t.estimatedHours === null ? '—' : num(t.estimatedHours, 1)} / {t.remainingHours === null ? '—' : num(t.remainingHours, 1)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Drawer>
  );
}
